import { create } from 'zustand';
import type { User, Session } from '@supabase/supabase-js';
import type { Profile, Workspace } from '../types/database';
import { setViewAsReadOnly, supabase } from '../lib/supabase';
import { WORKSPACE_FIELDS } from '../lib/workspaces';
import { identifyCrispUser, resetCrispSession } from '../lib/crisp';
import { trackRegistration } from '../lib/metaPixel';

const MARKETING_LOGIN_COOKIE = 'lima_logged_in';

const getMarketingCookieAttributes = () => {
  if (typeof window === 'undefined') return null;

  const hostname = window.location.hostname;
  const isLocalHost =
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === '0.0.0.0' ||
    hostname.endsWith('.localhost');

  const baseAttributes = ['Path=/', 'SameSite=Lax'];

  if (isLocalHost) {
    return {
      scoped: baseAttributes.join('; '),
      hostOnly: baseAttributes.join('; '),
    };
  }

  const isUseLimaDomain = hostname === 'uselima.com' || hostname.endsWith('.uselima.com');
  const secureAttributes = [...baseAttributes, 'Secure'];

  return {
    scoped: isUseLimaDomain
      ? [...baseAttributes, 'Domain=.uselima.com', 'Secure'].join('; ')
      : secureAttributes.join('; '),
    hostOnly: secureAttributes.join('; '),
  };
};

const syncMarketingCookie = (isLoggedIn: boolean) => {
  if (typeof document === 'undefined') return;

  const cookieAttributes = getMarketingCookieAttributes();
  if (!cookieAttributes) return;

  const maxAge = isLoggedIn ? 'Max-Age=2592000' : 'Max-Age=0';
  const value = isLoggedIn ? '1' : '';

  document.cookie = `${MARKETING_LOGIN_COOKIE}=${value}; ${cookieAttributes.scoped}; ${maxAge}`;

  if (cookieAttributes.hostOnly !== cookieAttributes.scoped) {
    document.cookie = `${MARKETING_LOGIN_COOKIE}=${value}; ${cookieAttributes.hostOnly}; ${maxAge}`;
  }
};

// Names the User a Staff member is viewing as. It only says which profile to load; the
// Staff read policies decide what the session can see. See docs/adr/0003.
const VIEW_AS_KEY = 'lima_view_as';

// Logs the View as, then reloads the app as that User. Throws when the database refuses.
export async function startViewAs(userId: string) {
  const { error } = await supabase.rpc('start_view_as', { target: userId });
  if (error) throw error;
  sessionStorage.setItem(VIEW_AS_KEY, userId);
  window.location.assign('/overview');
}

// Back to the Staff panel as the Staff themselves.
export function exitViewAs(userId: string) {
  sessionStorage.removeItem(VIEW_AS_KEY);
  window.location.assign(`/staff/users/${userId}`);
}

// The profile and current workspace of one User, or null when the profile can't be read.
// `workspaceId` loads another of their workspaces instead of the current one.
async function loadProfileAndWorkspace(userId: string, workspaceId?: string) {
  const { data: profile, error } = await supabase.from('profiles').select('*').eq('id', userId).maybeSingle();
  if (error) console.error('Error fetching profile:', error);
  if (!profile) return null;

  const id = workspaceId ?? profile.workspace_id;
  if (!id) return { profile, workspace: null };
  const { data: workspace } = await supabase
    .from('workspaces')
    .select(WORKSPACE_FIELDS)
    .eq('id', id)
    .maybeSingle()
    .overrideTypes<Workspace, { merge: false }>();
  return { profile, workspace: workspace ?? null };
}

interface AuthState {
  // The User the app renders as. During View as this is the viewed User (their id and
  // email on the Staff's auth user); `session` still belongs to the Staff.
  user: User | null;
  viewingAs: Profile | null;
  session: Session | null;
  profile: Profile | null;
  workspace: Workspace | null;
  loading: boolean;
  initialized: boolean;
  initializing: boolean;
  setUser: (user: User | null) => void;
  setSession: (session: Session | null) => void;
  setProfile: (profile: Profile | null) => void;
  setWorkspace: (workspace: Workspace | null) => void;
  setLoading: (loading: boolean) => void;
  initialize: () => Promise<void>;
  loadSessionUser: (authUser: User) => Promise<void>;
  fetchProfile: (userId: string) => Promise<void>;
  signOut: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  viewingAs: null,
  session: null,
  profile: null,
  workspace: null,
  loading: true,
  initialized: false,
  initializing: false,

  setUser: (user) => set({ user }),
  setSession: (session) => set({ session }),
  setProfile: (profile) => set({ profile }),
  setWorkspace: (workspace) => set({ workspace }),
  setLoading: (loading) => set({ loading }),

  initialize: async () => {
    // Prevent concurrent or duplicate initialization
    if (get().initialized || get().initializing) return;
    
    set({ initializing: true });

    try {
      // Set up auth listener FIRST - before getSession()
      // This ensures we catch PASSWORD_RECOVERY events from recovery links
      // IMPORTANT: Don't use await inside onAuthStateChange - it can cause hanging issues
      // See: https://supabase.com/docs/reference/javascript/auth-onauthstatechange
      supabase.auth.onAuthStateChange((_event, newSession) => {
        set({ session: newSession });
        syncMarketingCookie(Boolean(newSession?.user));

        if (newSession?.user) {
          // Don't await - fire and forget to avoid blocking
          get().loadSessionUser(newSession.user).catch(console.error);
        } else {
          set({ user: null, viewingAs: null, profile: null, workspace: null });
        }
      });

      // Get existing session after listener is set up
      const { data: { session } } = await supabase.auth.getSession();
      
      set({ session });
      syncMarketingCookie(Boolean(session?.user));

      if (session?.user) {
        await get().loadSessionUser(session.user);
      }
    } finally {
      set({ loading: false, initialized: true, initializing: false });
    }
  },

  // Loads whoever the session belongs to, or the User they view as when the View as key is
  // set and they are Staff. Anything else clears the key and loads their own profile.
  loadSessionUser: async (authUser: User) => {
    const viewAsId = sessionStorage.getItem(VIEW_AS_KEY);
    if (viewAsId && viewAsId !== authUser.id) {
      const own = await loadProfileAndWorkspace(authUser.id);
      // A token refresh mid-View as keeps the workspace switched to in memory.
      const { viewingAs, workspace } = get();
      const viewed = own?.profile.is_admin
        ? await loadProfileAndWorkspace(viewAsId, viewingAs?.id === viewAsId ? workspace?.id : undefined)
        : null;
      if (viewed) {
        set({
          user: { ...authUser, id: viewed.profile.id, email: viewed.profile.email ?? undefined },
          viewingAs: viewed.profile,
          ...viewed,
        });
        return;
      }
    }
    sessionStorage.removeItem(VIEW_AS_KEY);
    set({ user: authUser, viewingAs: null });
    await get().fetchProfile(authUser.id);
  },

  fetchProfile: async (userId: string) => {
    try {
      // View as never writes the profile, so a workspace switched to in memory is kept.
      const { viewingAs, workspace } = get();
      const loaded = await loadProfileAndWorkspace(userId, viewingAs ? workspace?.id : undefined);
      if (!loaded) return;

      set(loaded);
      if (!get().viewingAs && !loaded.profile.registration_tracked_at) trackRegistration().catch(console.error);
      // Runs on load and on each token refresh; View as never counts as the User being active.
      if (!get().viewingAs) supabase.rpc('touch_last_active').then(({ error }) => error && console.error(error));
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  },

  signOut: async () => {
    sessionStorage.removeItem(VIEW_AS_KEY);
    await supabase.auth.signOut();
    syncMarketingCookie(false);
    resetCrispSession();
    set({ user: null, viewingAs: null, session: null, profile: null, workspace: null });
  },
}));

useAuthStore.subscribe((state) => setViewAsReadOnly(!!state.viewingAs));

// Keeps the Crisp contact in step with whoever is signed in and their current workspace.
useAuthStore.subscribe((state, prev) => {
  const { user, profile, workspace, viewingAs } = state;
  if (!user || viewingAs) return;
  if (user === prev.user && profile === prev.profile && workspace === prev.workspace) return;
  identifyCrispUser({
    userId: user.id,
    email: profile?.email ?? user.email,
    name: [profile?.first_name, profile?.last_name].filter(Boolean).join(' '),
    avatarUrl: profile?.avatar_url,
    workspace,
  });
});
